/** VietQR bank-transfer dialog: creates a SePay order, shows the QR and transfer details, counts down, and polls until paid */

import { useEffect, useRef, useState } from 'react'
import { Check, CheckCircle2, Copy, Loader2 } from 'lucide-react'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { Modal } from '../ui/modal'
import { buttonPrimaryClass, buttonSecondaryClass, errorTextClass, hintClass, iconButtonClass } from '../ui/styles'
import { cn } from '../../lib/utils'
import { useCreateSepayOrder, useSepayOrderStatus, type CreditPack } from '../../hooks/use-credits'
import { formatCountdown, formatPrice } from './credit-format'

interface Props {
  pack: CreditPack
  onClose: () => void
}

function CopyRow({ label, value, display, mono }: { label: string; value: string; display?: string; mono?: boolean }) {
  const t = useT()
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard blocked: the value stays visible and selectable
    }
  }
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <dt className="text-xs text-fg-subtle">{label}</dt>
        <dd className={cn('break-all text-sm font-medium text-fg', mono && 'font-mono tracking-wide')}>{display ?? value}</dd>
      </div>
      <button
        type="button"
        onClick={() => void copy()}
        className={iconButtonClass}
        aria-label={copied ? t('billing.sepay.copied') : t('billing.sepay.copy', { label })}
        title={copied ? t('billing.sepay.copied') : t('billing.sepay.copy', { label })}
      >
        {copied ? <Check className="h-4 w-4 text-success" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  )
}

export function SepayTransferDialog({ pack, onClose }: Props) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const create = useCreateSepayOrder()
  const createOrder = create.mutate
  const started = useRef(false)
  const [now, setNow] = useState(() => Date.now())

  // One order per opening (a ref, so a StrictMode double effect does not create two)
  useEffect(() => {
    if (started.current) return
    started.current = true
    createOrder({ packId: pack.id })
  }, [createOrder, pack.id])

  const created = create.data
  const status = useSepayOrderStatus(created?.order.id)
  const order = status.data ?? created?.order
  const isPaid = order?.status === 'paid'
  const isExpired = !isPaid && !!order && (order.status === 'expired' || order.expiresAt <= now)

  useEffect(() => {
    if (!created || isPaid) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [created, isPaid])

  const numberFormat = new Intl.NumberFormat(locale)
  const amount = created ? formatPrice(created.order.amountVnd, 'vnd', locale) : ''

  return (
    <Modal title={t('billing.sepay.title', { name: pack.name })} onClose={onClose} widthClass="max-w-lg">
      {create.isPending || (!created && !create.isError) ? (
        <p className="flex items-center gap-2 py-6 text-sm text-fg-muted" role="status">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('billing.sepay.creating')}
        </p>
      ) : create.isError ? (
        <div className="space-y-4">
          <p className={errorTextClass} role="alert">
            {create.error.status === 503 ? t('billing.sepay.unconfigured') : `${t('billing.sepay.createFailed')} ${create.error.message}`}
          </p>
          <button type="button" className={buttonSecondaryClass} onClick={() => createOrder({ packId: pack.id })}>
            {t('billing.sepay.retry')}
          </button>
        </div>
      ) : created && isPaid ? (
        <div className="space-y-4 py-2" role="status">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-success" aria-hidden="true" />
            <div>
              <p className="font-semibold text-fg">{t('billing.sepay.paidTitle')}</p>
              <p className="mt-1 text-sm text-fg-muted">{t('billing.sepay.paidBody', { credits: numberFormat.format(created.credits) })}</p>
            </div>
          </div>
          <button type="button" className={cn(buttonPrimaryClass, 'w-full')} onClick={onClose} data-autofocus>
            {t('billing.sepay.done')}
          </button>
        </div>
      ) : created && order ? (
        <div className="space-y-4">
          <p className="text-sm text-fg-muted">{t('billing.sepay.scan')}</p>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <img
              src={created.qrUrl}
              alt={t('billing.sepay.qrAlt', { amount })}
              width={208}
              height={208}
              className={cn('h-52 w-52 shrink-0 rounded-md border border-border bg-white p-1', isExpired && 'opacity-40')}
            />
            <dl className="w-full min-w-0 divide-y divide-border">
              <div className="py-2">
                <dt className="text-xs text-fg-subtle">{t('billing.sepay.bank')}</dt>
                <dd className="text-sm font-medium text-fg">{created.bank.bankCode}</dd>
              </div>
              <CopyRow label={t('billing.sepay.account')} value={created.bank.accountNumber} mono />
              <div className="py-2">
                <dt className="text-xs text-fg-subtle">{t('billing.sepay.accountName')}</dt>
                <dd className="text-sm font-medium text-fg">{created.bank.accountName}</dd>
              </div>
              <CopyRow label={t('billing.sepay.amount')} value={String(created.order.amountVnd)} display={amount} />
              <CopyRow label={t('billing.sepay.content')} value={created.order.code} mono />
            </dl>
          </div>

          {isExpired ? (
            <div className="space-y-3">
              <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning" role="status">
                {t('billing.sepay.expired')}
              </p>
              <button type="button" className={buttonSecondaryClass} onClick={() => createOrder({ packId: pack.id })}>
                {t('billing.sepay.newOrder')}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm text-fg-muted" role="status" aria-live="polite">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {status.isError ? t('billing.sepay.statusError') : t('billing.sepay.waiting')}
              </p>
              <p className={cn(hintClass, 'tabular-nums')}>
                {t('billing.sepay.expiresIn', { time: formatCountdown(order.expiresAt - now) })}
              </p>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  )
}
